import { useState, useEffect, useRef, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFacilities } from '../hooks/useFacilities';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, X, Plus, ExternalLink } from 'lucide-react';
import { todayIST, nowIST } from '../../utils/ist';
import BookingDrawer from '../components/BookingDrawer';

// Locations loaded dynamically from /api/facilities

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Start hour from a start_time ("06:00" → 6) or slot_code ("0600-0700" → 6).
const slotStartHour = (t) => parseInt(String(t || '').replace(':', '').slice(0, 2), 10);

// A ground's active custom slots, ordered by start time.
function groundActiveSlots(ground) {
  return (ground?.slots || [])
    .filter(s => s.active !== false)
    .slice()
    .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
}

// Union of active slots across all grounds (de-duped by slot_code) — the Courts
// grid's row template, since grounds can have divergent custom slot lists.
function unionSlots(grounds) {
  const byCode = new Map();
  for (const g of grounds) {
    for (const s of groundActiveSlots(g)) if (!byCode.has(s.slot_code)) byCode.set(s.slot_code, s);
  }
  return [...byCode.values()].sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
}

// Format a Date object → YYYY-MM-DD in IST
// selectedDate carries the IST wall-clock in its LOCAL fields (from nowIST()),
// so read those fields directly — re-applying a timeZone here would double-convert
// and shift the day by one for browsers not running in IST.
function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

// ── Quick booking modal ────────────────────────────────────────────────────────
// Used when an available slot (or multiple) is selected in day/week view.
function QuickBookModal({ slots, date, locationId, groundId, api, onClose, onSuccess }) {
  const [form, setForm] = useState({ customer_name: '', customer_phone: '', notes: '' });
  const [payment, setPayment] = useState({ method: 'Cash', amount: '', status: 'Pending', transaction_id: '' });
  const [submitting, setSubmitting] = useState(false);

  const slotLabel = slots.length === 1
    ? slots[0].time
    : `${slots[0].start_time || slots[0].time} – ${slots[slots.length - 1].end_time || ''} (${slots.length} slots)`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim() || !form.customer_phone.trim()) {
      toast.error('Enter customer name and phone');
      return;
    }
    setSubmitting(true);
    try {
      const paymentData = payment.amount
        ? {
            method: payment.method,
            amount: parseFloat(payment.amount),
            status: payment.status,
            transaction_id: payment.method === 'UPI' ? payment.transaction_id : null,
          }
        : null;

      // Create one booking per selected slot (parallel)
      await Promise.all(slots.map(slot =>
        api.post('/api/bookings/', {
          location_id: locationId,
          ground_id: groundId,
          date,
          slot_id: slot.id,
          slot_time: slot.time,
          customer_name: form.customer_name.trim(),
          customer_phone: form.customer_phone.trim(),
          notes: form.notes.trim() || null,
          payment: paymentData,
        })
      ));

      toast.success(slots.length > 1 ? `${slots.length} slots booked` : 'Booking created');
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Slot already booked or error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="sb-overlay" onClick={onClose} />
      <div className="sb-modal" style={{ width: 460 }}>
        <div className="sb-modal-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div className="sb-modal-title">New Booking</div>
            <div className="sb-modal-sub">{slotLabel} · {date}</div>
          </div>
          <button onClick={onClose} className="sb-btn ghost icon"><X size={14} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="sb-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="sb-field">
                <label>Name *</label>
                <input type="text" value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} autoFocus required />
              </div>
              <div className="sb-field">
                <label>Phone *</label>
                <input type="tel" value={form.customer_phone} onChange={e => setForm({ ...form, customer_phone: e.target.value })} required />
              </div>
            </div>
            <div className="sb-field">
              <label>Notes <span style={{ color: 'var(--ink-4)', fontWeight: 400 }}>(optional)</span></label>
              <textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="e.g. Advance paid ₹290, Pending ₹300" style={{ resize: 'none' }} />
            </div>
            <div className="sb-field">
              <label>Payment <span style={{ color: 'var(--ink-4)', fontWeight: 400 }}>(optional)</span></label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div className="sb-chipset" style={{ flexShrink: 0 }}>
                  {['Cash', 'UPI'].map(m => (
                    <div key={m} className={`sb-chip ${payment.method === m ? 'active' : ''}`} onClick={() => setPayment({ ...payment, method: m })}>{m}</div>
                  ))}
                </div>
                <input type="number" value={payment.amount} onChange={e => setPayment({ ...payment, amount: e.target.value })} placeholder="₹ Amount" className="sb-form-input" style={{ flex: 1 }} />
              </div>
              {payment.amount && payment.method === 'UPI' && (
                <input type="text" value={payment.transaction_id} onChange={e => setPayment({ ...payment, transaction_id: e.target.value })} placeholder="Transaction ID" className="sb-form-input" style={{ marginTop: 8 }} />
              )}
            </div>
          </div>
          <div className="sb-modal-foot">
            <button type="button" onClick={onClose} className="sb-btn ghost">Cancel</button>
            <button type="submit" disabled={submitting} className="sb-btn brand">
              {submitting ? 'Booking…' : slots.length > 1 ? `Book ${slots.length} Slots` : 'Book Slot'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

// Booking detail is now handled by BookingDrawer (imported above)

// ── Main calendar ──────────────────────────────────────────────────────────────
async function loadCalendarData({ api, tab, selectedDate, locationId, groundId, setSlots, setBookings }) {
  // The Courts grid has its own fetch effect (all grounds); skip the per-ground loader.
  if (tab === 'grid') return;
  if (!groundId) return;

  if (tab === 'day') {
    const date = formatDate(selectedDate);
    const [slotsRes, bookingsRes] = await Promise.all([
      api.get('/api/slots/availability', { params: { location_id: locationId, ground_id: groundId, date } }),
      api.get('/api/bookings/', { params: { location_id: locationId, date } }),
    ]);
    setSlots(slotsRes.data);
    const dayArr = Array.isArray(bookingsRes.data) ? bookingsRes.data : (bookingsRes.data.bookings || []);
    setBookings(dayArr.filter(b => b.ground_id === groundId && b.status === 'confirmed'));
  } else {
    // week
    const weekStart = new Date(selectedDate);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    const dates = Array.from({ length: 7 }, (_, i) => formatDate(addDays(weekStart, i)));
    const results = await Promise.all(
      dates.map(d =>
        api.get('/api/bookings/', { params: { location_id: locationId, date: d } })
          .then(r => {
            const arr = Array.isArray(r.data) ? r.data : (r.data.bookings || []);
            return arr.filter(b => b.ground_id === groundId && b.status === 'confirmed');
          })
      )
    );
    setBookings(results.flat());
  }
}

export default function AdminCalendar() {
  const { user, api } = useAuth();
  const { facilities } = useFacilities();
  const navigate = useNavigate();

  const allowedLocations = (user?.role === 'owner'
    ? facilities
    : facilities.filter(f => user?.assigned_locations?.includes(f.location_id))
  ).map(f => ({ id: f.location_id, name: f.name }));

  const [tab, setTab] = useState('grid');
  const [selectedDate, setSelectedDate] = useState(nowIST());
  const [locationId, setLocationId] = useState(null);
  const [grounds, setGrounds] = useState([]);
  const [groundId, setGroundId] = useState('');
  const [bookings, setBookings] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gridBookings, setGridBookings] = useState([]); // all-grounds confirmed bookings for the Courts grid
  const [gridSelected, setGridSelected] = useState(new Set()); // `${groundId}|${slotId}` empty cells picked for booking

  // Set initial locationId once facilities load
  useEffect(() => {
    if (allowedLocations.length > 0 && locationId === null) {
      setLocationId(allowedLocations[0].id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedLocations.length]);

  // Day-view multi-slot selection
  const [selectedSlotIds, setSelectedSlotIds] = useState(new Set());

  // Modals
  const [bookModal, setBookModal] = useState(null);    // { slots, date }
  const [drawerBooking, setDrawerBooking] = useState(null); // booking for drawer

  const groundsCacheRef = useRef({});
  const groundIdRef = useRef(groundId);
  useEffect(() => { groundIdRef.current = groundId; }, [groundId]);

  // Clear slot selection when date/tab/ground changes
  useEffect(() => { setSelectedSlotIds(new Set()); }, [selectedDate, tab, groundId]);

  const refresh = async (gId = groundIdRef.current) => {
    setLoading(true);
    try {
      await loadCalendarData({ api, tab, selectedDate, locationId, groundId: gId, setSlots, setBookings });
    } catch (err) {
      if (err?.response?.status !== 401 && err?.response?.status !== 403)
        toast.error('Failed to refresh calendar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!locationId) return;
    let cancelled = false;
    setLoading(true);

    async function run() {
      let list = groundsCacheRef.current[locationId];
      if (!list) {
        const res = await api.get('/api/slots/grounds', { params: { location_id: locationId } });
        list = res.data;
        groundsCacheRef.current[locationId] = list;
      }
      if (cancelled) return;
      setGrounds(list);

      const currentGId = groundIdRef.current;
      const effectiveGId = list.some(g => g.id === currentGId) ? currentGId : (list[0]?.id || '');
      if (effectiveGId !== currentGId) { setGroundId(effectiveGId); groundIdRef.current = effectiveGId; }
      if (!effectiveGId || cancelled) return;

      await loadCalendarData({ api, tab, selectedDate, locationId, groundId: effectiveGId, setSlots, setBookings });
    }

    run()
      .catch(err => { if (!cancelled && err?.response?.status !== 401 && err?.response?.status !== 403) toast.error('Failed to load calendar'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, tab, selectedDate, api]);

  const handleGroundChange = async (newGId) => {
    setGroundId(newGId);
    groundIdRef.current = newGId;
    await refresh(newGId);
  };

  const bookingMap = bookings.reduce((acc, b) => {
    acc[`${b.date}-${b.slot_id}`] = b;
    return acc;
  }, {});

  // Clear grid selection whenever the day / location / view changes
  useEffect(() => { setGridSelected(new Set()); }, [selectedDate, locationId, tab]);

  const toggleGridCell = (key) => setGridSelected(prev => {
    const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n;
  });

  // Courts grid — fetch all-grounds confirmed bookings for the selected day
  useEffect(() => {
    if (tab !== 'grid' || !locationId) return;
    let cancelled = false;
    const date = formatDate(selectedDate);
    api.get('/api/bookings/', { params: { location_id: locationId, date } })
      .then(res => {
        if (cancelled) return;
        const arr = Array.isArray(res.data) ? res.data : (res.data.bookings || []);
        setGridBookings(arr.filter(b => b.status === 'confirmed'));
      })
      .catch(() => { if (!cancelled) setGridBookings([]); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, locationId, selectedDate, api]);

  // ── Courts grid view (court × slot matrix · SPEC §12) ──────────────────────────
  const renderGrid = () => {
    const date = formatDate(selectedDate);
    const todayStr = todayIST();
    const curHour = nowIST().getHours();
    const isPastSlot = (h) => date < todayStr || (date === todayStr && h < curHour);
    // booking lookup by ground+slot, and session slot-counts for +N
    const cellMap = {};
    const sessionCount = {};
    for (const b of gridBookings) {
      cellMap[`${b.ground_id}-${b.slot_id}`] = b;
      const k = b.session_id || b.booking_id;
      sessionCount[k] = (sessionCount[k] || 0) + 1;
    }
    if (grounds.length === 0) {
      return <p style={{ color: 'var(--ink-4)', textAlign: 'center', padding: '40px 0' }}>No courts configured for this facility.</p>;
    }
    const rows = unionSlots(grounds);
    if (rows.length === 0) {
      return <p style={{ color: 'var(--ink-4)', textAlign: 'center', padding: '40px 0' }}>No slots configured — add them in Facilities.</p>;
    }
    const selCount = gridSelected.size;
    const bookSelected = () => {
      const cells = [...gridSelected].map(k => { const [groundId, slotId] = k.split('|'); return { groundId, slotId }; });
      navigate('/admin/bookings/new', { state: { date, locationId, cells } });
    };
    return (
     <>
      {selCount > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'var(--warn-bg)', border: '1px solid var(--warn-fg)',
          borderRadius: 10, padding: '10px 14px', marginBottom: 12,
        }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--warn-fg)' }}>
            {selCount} slot{selCount > 1 ? 's' : ''} selected
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setGridSelected(new Set())} className="sb-btn ghost sm">Clear</button>
            <button onClick={bookSelected} className="sb-btn primary sm">
              <Plus size={12} /> Book {selCount} slot{selCount > 1 ? 's' : ''}
            </button>
          </div>
        </div>
      )}
      <div className="sb-cal-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <div className="sb-cal-grid" style={{ '--cols': grounds.length, minWidth: 80 + grounds.length * 130 }}>
          <div className="sb-cal-col-head" />
          {grounds.map(g => (
            <div key={g.id} className="sb-cal-col-head">
              {g.name}{g.sport && <span className="sport">{g.sport}</span>}
            </div>
          ))}
          {rows.map(row => {
            const sid = row.slot_code;
            const startH = slotStartHour(row.start_time);
            return (
              <Fragment key={sid}>
                <div className="sb-cal-row-head">{row.start_time}</div>
                {grounds.map(g => {
                  // This court may not offer this slot (divergent custom slot lists).
                  const offered = groundActiveSlots(g).some(s => s.slot_code === sid);
                  if (!offered) {
                    return (
                      <div key={g.id} className="sb-cal-cell" title="Not offered on this court"
                        style={{ background: 'var(--surface-2)', opacity: 0.3, cursor: 'default' }} />
                    );
                  }
                  const b = cellMap[`${g.id}-${sid}`];
                  if (b) {
                    const pendingPay = (b.payment?.status || 'Pending') !== 'Paid';
                    const n = (sessionCount[b.session_id || b.booking_id] || 1) - 1;
                    return (
                      <div key={g.id} className={`sb-cal-cell ${pendingPay ? 'pending' : 'booked'}`}
                        style={{ cursor: 'pointer' }} onClick={() => setDrawerBooking(b)}>
                        <div className="who">{b.customer_name}</div>
                        <div className="who-small">
                          {row.start_time}–{row.end_time}{n > 0 && <span style={{ opacity: 0.75 }}> · +{n}</span>}
                        </div>
                      </div>
                    );
                  }
                  const key = `${g.id}|${sid}`;
                  const sel = gridSelected.has(key);
                  if (isPastSlot(startH)) {
                    return (
                      <div key={g.id} className="sb-cal-cell"
                        title="Past — can't book"
                        style={{ background: 'var(--surface-2)', opacity: 0.5, cursor: 'not-allowed' }} />
                    );
                  }
                  return (
                    <div key={g.id} className="sb-cal-cell" title="Click to select for booking"
                      onClick={() => toggleGridCell(key)}
                      style={{
                        cursor: 'pointer',
                        background: sel ? 'color-mix(in oklch, var(--accent) 22%, var(--surface))' : undefined,
                        boxShadow: sel ? 'inset 0 0 0 2px var(--accent)' : undefined,
                      }}>
                      {sel && <span className="sb-mono" style={{ fontSize: 10.5, color: 'var(--accent-ink)', fontWeight: 600 }}>✓</span>}
                    </div>
                  );
                })}
              </Fragment>
            );
          })}
        </div>
      </div>
     </>
    );
  };

  // ── Day view ─────────────────────────────────────────────────────────────────
  const renderDay = () => {
    const date = formatDate(selectedDate);
    const hasSelection = selectedSlotIds.size > 0;

    if (loading) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '10px 14px', background: 'var(--surface-2)', borderRadius: 9 }}>
              <div style={{ height: 12, background: 'var(--line)', borderRadius: 4, width: 112 }} />
              <div style={{ height: 12, background: 'var(--line)', borderRadius: 4, width: 144 }} />
            </div>
          ))}
        </div>
      );
    }

    if (slots.length === 0) {
      return <p style={{ color: 'var(--ink-4)', textAlign: 'center', padding: '40px 0' }}>No slots configured for this ground.</p>;
    }

    const openBookModal = () => {
      const ordered = slots.filter(s => selectedSlotIds.has(s.id));
      setBookModal({ slots: ordered, date });
      setSelectedSlotIds(new Set());
    };

    const openFullForm = () => {
      // Pre-fill first selected slot; full form handles multi-slot via the grid
      const firstSlotId = [...selectedSlotIds].sort()[0];
      navigate('/admin/bookings/new', {
        state: { date, groundId, locationId, slotId: firstSlotId },
      });
    };

    const payBadge = (status) => status === 'Paid' ? 'ok' : status === 'Refunded' ? 'info' : 'warn';

    return (
      <div>
        {/* Selection action bar */}
        {hasSelection && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: 'var(--warn-bg)', border: '1px solid var(--warn-fg)',
            borderRadius: 10, padding: '10px 14px', marginBottom: 12,
          }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--warn-fg)' }}>
              {selectedSlotIds.size} slot{selectedSlotIds.size > 1 ? 's' : ''} selected
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setSelectedSlotIds(new Set())} className="sb-btn ghost sm">Clear</button>
              <button onClick={openBookModal} className="sb-btn primary sm">
                <Plus size={12} /> Quick Book
              </button>
              <button onClick={openFullForm} className="sb-btn sm" title="Open full booking form with these slots pre-selected">
                <ExternalLink size={12} /> Full Form
              </button>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {slots.map(slot => {
            const booking = bookingMap[`${date}-${slot.id}`];
            const isSelected = selectedSlotIds.has(slot.id);

            if (booking) {
              return (
                <button
                  key={slot.id}
                  onClick={() => setDrawerBooking(booking)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', padding: '10px 14px',
                    borderRadius: 9, border: '1px solid var(--brand)', cursor: 'pointer',
                    background: 'oklch(from var(--brand) l c h / 0.08)', textAlign: 'left',
                    transition: 'background .1s',
                  }}
                >
                  <span className="sb-mono" style={{ fontSize: 12.5, color: 'var(--ink-3)', width: 140, flexShrink: 0 }}>{slot.time}</span>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink)' }}>{booking.customer_name}</span>
                    <span className="sb-mono" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{booking.customer_phone}</span>
                    <span className={`sb-badge ${payBadge(booking.payment?.status)}`}>{booking.payment?.status || 'Pending'}</span>
                    {booking.payment?.amount && (
                      <span className="sb-mono" style={{ fontSize: 12, color: 'var(--ink-2)' }}>₹{booking.payment.amount}</span>
                    )}
                  </div>
                </button>
              );
            }

            return (
              <button
                key={slot.id}
                onClick={() => {
                  setSelectedSlotIds(prev => {
                    const next = new Set(prev);
                    next.has(slot.id) ? next.delete(slot.id) : next.add(slot.id);
                    return next;
                  });
                }}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', padding: '10px 14px',
                  borderRadius: 9, border: `1px solid ${isSelected ? 'var(--ok-fg)' : 'var(--line)'}`,
                  cursor: 'pointer', background: isSelected ? 'oklch(from var(--ok-fg) l c h / 0.08)' : 'var(--surface-2)',
                  textAlign: 'left', transition: 'background .1s, border-color .1s',
                }}
              >
                <span className="sb-mono" style={{ fontSize: 12.5, color: isSelected ? 'var(--ok-fg)' : 'var(--ink-3)', width: 140, flexShrink: 0 }}>{slot.time}</span>
                <span style={{ fontSize: 12, color: isSelected ? 'var(--ok-fg)' : 'var(--ink-4)', fontWeight: isSelected ? 600 : 400 }}>
                  {isSelected ? '✓ Selected' : 'Available'}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Week view ─────────────────────────────────────────────────────────────────
  const renderWeek = () => {
    const weekStart = new Date(selectedDate);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    const dates = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    const today = todayIST();
    const weekSlots = groundActiveSlots(grounds.find(g => g.id === groundId));

    if (weekSlots.length === 0) {
      return <p style={{ color: 'var(--ink-4)', textAlign: 'center', padding: '40px 0' }}>No slots configured for this ground.</p>;
    }

    return (
      <div style={{ overflowX: 'auto', opacity: loading ? 0.5 : 1, pointerEvents: loading ? 'none' : 'auto', transition: 'opacity .15s' }}>
        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '6px 8px 8px', width: 80, color: 'var(--ink-3)', fontWeight: 500 }}>Time</th>
              {dates.map(d => {
                const ds = formatDate(d);
                const isToday = ds === today;
                return (
                  <th key={ds} style={{ padding: '6px 4px 8px', textAlign: 'center', minWidth: 80 }}>
                    <button
                      onClick={() => { setSelectedDate(d); setTab('day'); }}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                        color: isToday ? 'var(--brand)' : 'var(--ink-3)', fontWeight: isToday ? 700 : 500,
                        borderRadius: 6,
                      }}
                    >
                      {DAYS[d.getDay()]}<br />
                      <span>{d.getDate()}</span>
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {weekSlots.map(slot => {
              const slotId = slot.slot_code;
              const time = `${slot.start_time}–${slot.end_time}`;
              return (
                <tr key={slotId} style={{ borderTop: '1px solid var(--line-soft)' }}>
                  <td className="sb-mono" style={{ color: 'var(--ink-4)', padding: '4px 8px', fontSize: 11, whiteSpace: 'nowrap' }}>{time}</td>
                  {dates.map(d => {
                    const dateStr = formatDate(d);
                    const booking = bookingMap[`${dateStr}-${slotId}`];
                    return (
                      <td key={dateStr} style={{ padding: '2px 3px' }}>
                        {booking ? (
                          <button
                            onClick={() => setDrawerBooking(booking)}
                            title={`${booking.customer_name} — click to view`}
                            style={{
                              width: '100%', background: 'var(--brand)', borderRadius: 5,
                              color: 'var(--brand-ink)', padding: '4px 6px', fontSize: 11,
                              border: 'none', cursor: 'pointer', textAlign: 'left',
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                              fontWeight: 600,
                            }}
                          >
                            {booking.customer_name.split(' ')[0]}
                          </button>
                        ) : (
                          <button
                            onClick={() => navigate('/admin/bookings/new', {
                              state: { date: dateStr, groundId, locationId, slotId },
                            })}
                            title={`New booking — ${time} on ${dateStr}`}
                            style={{
                              width: '100%', height: 24, borderRadius: 5, border: '1px solid transparent',
                              background: 'transparent', cursor: 'pointer', color: 'var(--ink-4)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              transition: 'background .1s, border-color .1s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-2)'; e.currentTarget.style.borderColor = 'var(--line)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; }}
                          >
                            <Plus size={10} />
                          </button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  // d's local fields already hold the IST wall-clock — format without a timeZone to avoid double-conversion.
  const fmtNav = (d) => d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
  const dateLabel = tab === 'week'
    ? (() => {
        const ws = new Date(selectedDate);
        ws.setDate(ws.getDate() - ws.getDay());
        const we = addDays(ws, 6);
        return `${fmtNav(ws)} – ${fmtNav(we)}`;
      })()
    : fmtNav(selectedDate);

  return (
    <div>
      <div className="sb-page-head">
        <div>
          <div className="sb-crumb">Operations</div>
          <h1>Calendar</h1>
          <div className="sb-sub">Click any open slot to start a booking. Pending payments shown in amber.</div>
        </div>
      </div>

      {/* Controls */}
      <div className="sb-toolbar" style={{ flexWrap: 'wrap', flexDirection: 'column', alignItems: 'flex-start', gap: 10 }}>
        {/* Location pills — only show if more than one location */}
        {allowedLocations.length > 1 && (
          <div className="sb-chipset">
            {allowedLocations.map(l => (
              <div
                key={l.id}
                className={`sb-chip ${locationId === l.id ? 'active' : ''}`}
                onClick={() => setLocationId(l.id)}
              >
                {l.name}
              </div>
            ))}
          </div>
        )}

        {/* Ground pills — only for per-court Day/Week views (grid shows all courts) */}
        {tab !== 'grid' && grounds.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: '.12em', textTransform: 'uppercase', flexShrink: 0 }}>Ground</span>
            <div className="sb-chipset">
              {grounds.map(g => (
                <div key={g.id} className={`sb-chip ${groundId === g.id ? 'active' : ''}`} onClick={() => handleGroundChange(g.id)}>
                  {g.name}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Date nav — Yesterday / [date] / Tomorrow (week shifts ±7) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <button onClick={() => setSelectedDate(d => addDays(d, tab === 'week' ? -7 : -1))} className="sb-btn">
            ← {tab === 'week' ? 'Prev week' : 'Yesterday'}
          </button>
          <button onClick={() => setSelectedDate(nowIST())} className="sb-btn primary sb-mono" style={{ minWidth: 150, justifyContent: 'center' }}>
            {dateLabel}
          </button>
          <button onClick={() => setSelectedDate(d => addDays(d, tab === 'week' ? 7 : 1))} className="sb-btn">
            {tab === 'week' ? 'Next week' : 'Tomorrow'} →
          </button>
          {tab === 'grid' && grounds.length > 0 && (
            <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--ink-3)' }}>
              <span className="sb-mono">{gridBookings.length}</span> booked of <span className="sb-mono">{grounds.reduce((n, g) => n + groundActiveSlots(g).length, 0)}</span> slots
            </span>
          )}
        </div>
      </div>

      {/* Tab switcher — day / week / courts grid */}
      <div className="sb-chipset" style={{ marginBottom: 16 }}>
        {[['grid', 'Courts'], ['day', 'Day'], ['week', 'Week']].map(([t, label]) => (
          <div key={t} className={`sb-chip ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}
            style={{ padding: '6px 16px' }}>
            {label}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12, fontSize: 12, color: 'var(--ink-3)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: 'var(--brand)', display: 'inline-block', opacity: .7 }} />
          Booked — click to view
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: 'var(--surface)', border: '1px solid var(--line)', display: 'inline-block' }} />
          {tab === 'day' ? 'Available — click to select, then Book' : 'Available — click to book'}
        </span>
      </div>

      <div className="sb-card" style={{ padding: 16 }}>
        {tab === 'grid' ? renderGrid() : tab === 'day' ? renderDay() : renderWeek()}
      </div>

      {/* Quick booking modal */}
      {bookModal && (
        <QuickBookModal
          slots={bookModal.slots}
          date={bookModal.date}
          locationId={locationId}
          groundId={groundId}
          api={api}
          onClose={() => setBookModal(null)}
          onSuccess={() => refresh()}
        />
      )}

      {/* Booking detail drawer */}
      {drawerBooking && (
        <BookingDrawer
          booking={drawerBooking}
          facilityName={allowedLocations.find(l => l.id === locationId)?.name}
          slotTotal={0}
          api={api}
          onClose={() => setDrawerBooking(null)}
          onMutated={() => { refresh(); setDrawerBooking(null); }}
        />
      )}
    </div>
  );
}
